import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  FileText, Plus, Trash2, Loader2, Building2, ArrowLeft, ArrowRight,
  Calendar, User, IndianRupee, Download, Eye, CheckCircle2, AlertCircle, BookmarkPlus,
  PartyPopper, Share2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { formatIndianDate } from "../utils/format";
import RegistrationWall from "../components/RegistrationWall";
import SEO from "../seo/SEO";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

function HSNInput({ value, onChange }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/masters/hsn`, {
          params: { search: query, limit: 6 }, withCredentials: true,
        });
        setResults(res.data.items || []);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); }}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search HSN..."
        className="text-sm font-mono"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-lg max-h-44 overflow-y-auto">
          {results.map(h => (
            <button
              key={h.code}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onChange(h.code, h.gst_rate); setQuery(h.code); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs border-b border-slate-50"
            >
              <span className="font-mono font-bold text-slate-900">{h.code}</span>
              <span className="text-slate-500 ml-1.5">{h.description}</span>
              <span className="text-blue-600 ml-1.5 font-bold">{h.gst_rate}%</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// GST validation: 2 digits + 10 PAN chars + 1 digit + Z + 1 alphanumeric
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
function validateGSTIN(gstin) {
  if (!gstin || gstin.length === 0) return null; // empty is ok (optional)
  if (gstin.length !== 15) return "GSTIN must be exactly 15 characters";
  if (!GSTIN_REGEX.test(gstin)) return "Invalid GSTIN format (e.g. 29AABCT1234A1Z5)";
  return null;
}

// Amount to Indian words
const _ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
const _tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
function _below100(n) { return n < 20 ? _ones[n] : (_tens[Math.floor(n/10)] + (n%10 ? " "+_ones[n%10] : "")); }
function _below1000(n) { return n < 100 ? _below100(n) : _ones[Math.floor(n/100)] + " Hundred" + (n%100 ? " and " + _below100(n%100) : ""); }
function amountInWords(amount) {
  if (!amount || amount === 0) return "Zero Rupees Only";
  let r = Math.floor(Math.abs(amount));
  const p = Math.round((Math.abs(amount) - r) * 100);
  const parts = [];
  if (r >= 10000000) { parts.push(_below1000(Math.floor(r/10000000)) + " Crore"); r %= 10000000; }
  if (r >= 100000) { parts.push(_below100(Math.floor(r/100000)) + " Lakh"); r %= 100000; }
  if (r >= 1000) { parts.push(_below100(Math.floor(r/1000)) + " Thousand"); r %= 1000; }
  if (r > 0) parts.push(_below1000(r));
  let result = parts.join(" ") + " Rupees";
  if (p > 0) result += " and " + _below100(p) + " Paise";
  return result + " Only";
}

export default function InvoiceBuilderPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1); // 1=setup, 2=items, 3=preview
  const [stateType, setStateType] = useState("intra"); // intra or inter

  const [form, setForm] = useState({
    company_id: "",
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "",
    customer_name: "",
    customer_gst: "",
    customer_address: "",
    customer_email: "",
    customer_phone: "",
    customer_id: "",
    invoice_type: "sales",
    notes: "",
    terms: "Payment due within 30 days of invoice date.",
    discount: 0,
    shipping: 0,
    round_off: 0,
    previous_balance: 0,
    place_of_supply: "",
    tds_enabled: false,
    tds_section: "",
    tds_rate: 0,
    tcs_enabled: false,
    tcs_rate: 0,
    // Linked quotations for milestone billing
    source_quotation_ids: [],
    // Make this recurring (P2.8 — simplified recurring flow)
    make_recurring: false,
    rec_frequency: "monthly",
    rec_start_date: "",
    rec_end_date: "",
    rec_auto_send: false,
  });

  // Active quotations available for the current customer (lazy-loaded)
  const [availableQuotations, setAvailableQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  const [items, setItems] = useState([
    { name: "", description: "", hsn_code: "", quantity: 1, unit: "", kind: "product", unit_price: 0, gst_percent: 18, gst_not_applicable: false, save_to_master: true }
  ]);
  // Save-customer preference (checkbox at bottom of customer section)
  const [saveCustomerToMaster, setSaveCustomerToMaster] = useState(true);

  const [products, setProducts] = useState([]);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [tdsSections, setTdsSections] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custSearch, setCustSearch] = useState("");
  const [showCustDropdown, setShowCustDropdown] = useState(false);
  const [showAddCust, setShowAddCust] = useState(false);

  // ── Quick Invoice / Progressive Seller Capture ──
  const [inlineSeller, setInlineSeller] = useState({
    company_name: "",
    gst_number: "",
    address: "",
    state: "",
    phone: "",
    email: "",
  });
  const [sellerPersisted, setSellerPersisted] = useState(false);

  // ── Anonymous / Registration Wall state ──
  // Frictionless Invoice Mode — visitor can build an invoice without signup;
  // gated actions (Download PDF, Share, Save, Save-to-Master) open the wall.
  const isAnonymous = !isAuthenticated;
  const DRAFT_KEY = "invoicesetu.anon_draft.v1";
  const PENDING_INTENT_KEY = "invoicesetu.anon_pending_intent.v1";
  const [showRegWall, setShowRegWall] = useState(false);
  const [pendingIntent, setPendingIntent] = useState(null); // "download" | "share" | "save" | "save_master"

  useEffect(() => {
    if (isAuthenticated) {
      fetchCompanies();
      fetchProducts();
      fetchTdsSections();
      fetchCustomers();
      // GA4: activation event — invoice started (logged-in flow)
      try { window.gtag?.("event", "first_invoice_started"); } catch { /* ignore */ }
    } else {
      // Anonymous mode — no API calls, hydrate from localStorage if a draft exists
      setLoading(false);
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft?.form) setForm(f => ({ ...f, ...draft.form }));
          if (Array.isArray(draft?.items) && draft.items.length) setItems(draft.items);
          if (draft?.inlineSeller) setInlineSeller(s => ({ ...s, ...draft.inlineSeller }));
          if (draft?.stateType) setStateType(draft.stateType);
        }
      } catch { /* corrupt draft — ignore */ }
      // GA4: track anonymous invoice start (fires once per session via sessionStorage guard)
      try {
        if (!sessionStorage.getItem("anon_started_fired")) {
          window.gtag?.("event", "anonymous_invoice_started");
          sessionStorage.setItem("anon_started_fired", "1");
        }
      } catch { /* ignore */ }
    }
  }, [isAuthenticated]);

  // Persist anonymous draft on any change (debounced by React batching)
  useEffect(() => {
    if (!isAnonymous) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        form, items, inlineSeller, stateType, savedAt: new Date().toISOString(),
      }));
    } catch { /* quota — ignore */ }
  }, [isAnonymous, form, items, inlineSeller, stateType]);

  // Resume the pending action after signup/login
  useEffect(() => {
    if (!isAuthenticated) return;
    let pending;
    try {
      const raw = localStorage.getItem(PENDING_INTENT_KEY);
      if (!raw) return;
      pending = JSON.parse(raw);
      // Only resume within 30 minutes of the wall being shown
      if (!pending?.intent || Date.now() - (pending.at || 0) > 30 * 60 * 1000) {
        localStorage.removeItem(PENDING_INTENT_KEY);
        return;
      }
    } catch { return; }

    // Rehydrate the draft into local state first, then trigger the pending action
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft?.form) setForm(f => ({ ...f, ...draft.form }));
        if (Array.isArray(draft?.items) && draft.items.length) setItems(draft.items);
        if (draft?.inlineSeller) setInlineSeller(s => ({ ...s, ...draft.inlineSeller }));
        if (draft?.stateType) setStateType(draft.stateType);
      }
    } catch { /* corrupt — ignore */ }

    // Clear intent immediately so a second render doesn't re-fire, and set a one-shot trigger
    localStorage.removeItem(PENDING_INTENT_KEY);
    try { window.gtag?.("event", "signup_after_invoice", { intent: pending.intent }); } catch { /* ignore */ }
    // Kick off save on the next tick (state has settled)
    setTimeout(() => {
      // handleSave uses the latest state closure; wrap in a re-fetch of the ref via requestAnimationFrame
      requestAnimationFrame(() => {
        // Show a friendly success banner
        toast.success("Welcome back! Finishing what you started…", { duration: 3500 });
        handleSave();
      });
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (location.state?.company) {
      setForm(prev => ({ ...prev, company_id: location.state.company.company_id }));
    }
  }, [location.state]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompanies(res.data.companies || []);
      if (res.data.companies?.length === 1 && !form.company_id) {
        setForm(prev => ({ ...prev, company_id: res.data.companies[0].company_id }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectedCompany = companies.find(c => c.company_id === form.company_id);
  const isQuickInvoiceMode = isAnonymous || (companies.length === 0 && !form.company_id);
  // Non-GST mode: company explicitly flagged or has no GSTIN.
  // For quick-invoice mode: use inlineSeller.gst_number presence.
  const isGstRegistered = isQuickInvoiceMode
    ? !!(inlineSeller.gst_number && inlineSeller.gst_number.trim())
    : (selectedCompany
        ? (selectedCompany.gst_registered !== undefined
            ? !!selectedCompany.gst_registered
            : !!(selectedCompany.gst_number && selectedCompany.gst_number.trim()))
        : true);

  // Auto-calculate due date from company payment terms
  useEffect(() => {
    if (selectedCompany && form.invoice_date && !form.due_date) {
      const days = selectedCompany.payment_terms_days || 30;
      const invDate = new Date(form.invoice_date);
      if (!isNaN(invDate.getTime())) {
        invDate.setDate(invDate.getDate() + days);
        setForm(prev => ({ ...prev, due_date: invDate.toISOString().split("T")[0] }));
      }
    }
  }, [selectedCompany, form.invoice_date]);

  // Also set default notes/terms from company
  useEffect(() => {
    if (selectedCompany) {
      const updates = {};
      if (selectedCompany.invoice_notes && !form.notes) {
        updates.notes = selectedCompany.invoice_notes;
      }
      if (selectedCompany.payment_terms) {
        const v = String(selectedCompany.payment_terms).trim();
        let humanTerm;
        if (/^due on receipt$/i.test(v)) humanTerm = "Payment Due On Receipt";
        else {
          const m = v.match(/^net\s+(\d+)$/i);
          humanTerm = m ? `Payment Due Within ${m[1]} Days`
                        : (selectedCompany.payment_terms_days ? `Payment Due Within ${selectedCompany.payment_terms_days} Days` : v);
        }
        updates.terms = humanTerm;
      }
      if (Object.keys(updates).length) {
        setForm(prev => ({ ...prev, ...updates }));
      }
    }
  }, [selectedCompany?.company_id]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/masters/products`);
      setProducts(res.data.products || []);
    } catch { /* ignore */ }
  };

  const fetchTdsSections = async () => {
    try {
      const res = await axios.get(`${API}/receivables/tds-sections`, { withCredentials: true });
      setTdsSections(res.data.sections || []);
    } catch { /* ignore */ }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers/`, { withCredentials: true });
      setCustomers(res.data.customers || []);
    } catch { /* ignore */ }
  };

  const selectCustomer = (c) => {
    setForm(prev => ({
      ...prev,
      customer_name: c.name,
      customer_gst: c.gstin || "",
      customer_address: c.address || "",
      customer_email: c.email || "",
      customer_phone: c.phone || "",
      customer_id: c.customer_id,
      place_of_supply: c.state || "",
    }));
    setCustSearch(c.name);
    setShowCustDropdown(false);
    // Auto-detect state type
    if (c.gstin && c.gstin.length === 15 && form.company_id) {
      const comp = companies.find(co => co.company_id === form.company_id);
      if (comp && comp.gst_number) {
        setStateType(comp.gst_number.substring(0, 2) === c.gstin.substring(0, 2) ? "intra" : "inter");
      }
    }
    // Fetch pending balance for this customer
    if (c.customer_id) {
      fetchCustomerBalance(c.customer_id);
    }
  };

  const [pendingBalance, setPendingBalance] = useState(null);
  const [includeBalance, setIncludeBalance] = useState(false);

  const fetchCustomerBalance = async (customerId) => {
    try {
      const res = await axios.get(`${API}/builder/customer-balance/${customerId}`, { withCredentials: true });
      const bal = res.data.pending_balance || 0;
      setPendingBalance({ amount: bal, count: res.data.unpaid_invoice_count || 0 });
      if (bal > 0) {
        // Don't auto-include, but show it
        setIncludeBalance(false);
      }
    } catch {
      setPendingBalance(null);
    }
  };

  // When includeBalance toggles, update previous_balance in form
  useEffect(() => {
    if (pendingBalance && includeBalance) {
      setForm(prev => ({ ...prev, previous_balance: pendingBalance.amount }));
    } else if (!includeBalance) {
      setForm(prev => ({ ...prev, previous_balance: 0 }));
    }
  }, [includeBalance]);

  const quickAddCustomer = async () => {
    if (!form.customer_name.trim()) return;
    try {
      const res = await axios.post(`${API}/customers/`, {
        name: form.customer_name,
        gstin: form.customer_gst || null,
        address: form.customer_address || null,
        email: form.customer_email || null,
        phone: form.customer_phone || null,
        company_id: form.company_id,
      }, { withCredentials: true });
      toast.success(`"${res.data.name}" saved to customers`);
      setForm(prev => ({ ...prev, customer_id: res.data.customer_id }));
      setShowAddCust(false);
      fetchCustomers();
    } catch { toast.error("Failed to save customer"); }
  };

  const selectProduct = (index, product) => {
    setItems(prev => prev.map((item, i) => i === index ? {
      ...item,
      name: product.name,
      hsn_code: product.hsn_code || "",
      unit: product.unit || item.unit || "",
      kind: product.kind || item.kind || "product",
      unit_price: product.default_price || item.unit_price,
      gst_percent: product.default_gst_rate || item.gst_percent,
    } : item));
  };

  const isInCatalog = (item) => products.some(p => p.name === item.name);

  const saveToCatalog = async (item) => {
    try {
      await axios.post(`${API}/masters/products`, {
        name: item.name,
        hsn_code: item.hsn_code || "",
        unit: item.unit || null,
        kind: item.kind || "product",
        default_gst_rate: item.gst_percent || 18,
        default_price: item.unit_price || null,
      }, { withCredentials: true });
      toast.success(`"${item.name}" saved to catalog`);
      fetchProducts();
    } catch {
      toast.error("Failed to save to catalog");
    }
  };

  // Auto-detect state type when customer GSTIN changes
  useEffect(() => {
    if (selectedCompany?.gst_number && form.customer_gst && form.customer_gst.length >= 2) {
      const sellerState = selectedCompany.gst_number.substring(0, 2);
      const buyerState = form.customer_gst.substring(0, 2);
      setStateType(sellerState === buyerState ? "intra" : "inter");
    }
  }, [form.customer_gst, selectedCompany]);

  // Lazy-load active quotations for milestone-billing linkage
  useEffect(() => {
    const gst = (form.customer_gst || "").trim();
    const email = (form.customer_email || "").trim();
    const name = (form.customer_name || "").trim();
    if (!gst && !email && !name) {
      setAvailableQuotations([]);
      return;
    }
    const params = new URLSearchParams();
    if (gst) params.set("customer_gst", gst);
    if (email) params.set("customer_email", email);
    if (name) params.set("customer_name", name);
    if (form.company_id) params.set("company_id", form.company_id);
    setLoadingQuotations(true);
    axios
      .get(`${API}/quotations/active-for-customer?${params}`, { withCredentials: true })
      .then((r) => setAvailableQuotations(r.data?.quotations || []))
      .catch(() => setAvailableQuotations([]))
      .finally(() => setLoadingQuotations(false));
  }, [form.customer_gst, form.customer_email, form.customer_name, form.company_id]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const updateItem = (index, key, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [key]: value } : item));
  };

  const addItem = () => setItems(prev => [...prev, { name: "", description: "", hsn_code: "", quantity: 1, unit: "", kind: "product", unit_price: 0, gst_percent: selectedCompany?.default_gst_percent || 18, gst_not_applicable: false, line_discount: 0, line_discount_percent: 0, save_to_master: true }]);
  const removeItem = (index) => { if (items.length > 1) setItems(prev => prev.filter((_, i) => i !== index)); };

  const totals = useMemo(() => {
    // Safe number parser — converts any value to a safe finite number
    const safe = (v) => { const n = Number(v); return (isFinite(n) && !isNaN(n)) ? n : 0; };

    // Pass 1 — compute per-item post-line-discount; accumulate pools
    let rawSubtotal = 0;            // sum of all GST item gross (pre-any-discount)
    let nonGstTotal = 0;            // sum of non-GST items AFTER their own line discount
    let postLinePool = 0;           // sum of GST items AFTER line-level discounts (for overall distribution)
    let totalLineDiscount = 0;      // total ₹ of line-level discounts across GST items

    const enriched = items.map(item => {
      const isSvc = (item.kind || "product") === "service";
      const qty = isSvc ? 1 : safe(item.quantity);
      const rate = safe(item.unit_price);
      const gross = qty * rate;
      const ldPct = safe(item.line_discount_percent);
      const ldFlat = safe(item.line_discount);
      const derivedFromPct = ldPct ? Math.round(gross * ldPct) / 100 : 0;
      const lineDisc = Math.min(Math.max(ldFlat, derivedFromPct), gross);

      if (item.gst_not_applicable) {
        const effective = gross - lineDisc;
        nonGstTotal += effective;
        return { ...item, _gross: gross, _lineDisc: lineDisc, _postLine: effective };
      }
      rawSubtotal += gross;
      postLinePool += (gross - lineDisc);
      totalLineDiscount += lineDisc;
      return { ...item, _gross: gross, _lineDisc: lineDisc, _postLine: gross - lineDisc };
    });

    // Pass 2 — overall discount proportionally distributed across post-line pool
    const overall = Math.min(safe(form.discount), postLinePool);
    const overallRatio = postLinePool > 0 ? overall / postLinePool : 0;

    let taxAmount = 0;
    let discountedSubtotal = 0;
    const hsnMap = {};

    enriched.forEach(item => {
      if (item.gst_not_applicable) return;
      const gstPct = safe(item.gst_percent);
      const overallShare = Math.round(item._postLine * overallRatio * 100) / 100;
      const taxable = Math.max(0, Math.round((item._postLine - overallShare) * 100) / 100);
      const tax = Math.round(taxable * gstPct / 100 * 100) / 100;
      discountedSubtotal += taxable;
      taxAmount += tax;

      // HSN summary — uses POST-both-discounts taxable
      const code = item.hsn_code || "N/A";
      if (!hsnMap[code]) hsnMap[code] = { code, taxableValue: 0, rate: gstPct, cgst: 0, sgst: 0, igst: 0, total: 0 };
      hsnMap[code].taxableValue += taxable;
      const isIntra = stateType === "intra";
      if (isIntra) { hsnMap[code].cgst += tax / 2; hsnMap[code].sgst += tax / 2; }
      else { hsnMap[code].igst += tax; }
      hsnMap[code].total += taxable + tax;
    });

    if (!isGstRegistered) taxAmount = 0;  // Non-GST company — zero out tax for summary clarity

    const totalInclTax = discountedSubtotal + taxAmount;
    const shipping = safe(form.shipping);
    const roundOff = safe(form.round_off);
    const previousBalance = safe(form.previous_balance);
    const grandTotal = totalInclTax + nonGstTotal + shipping + roundOff + previousBalance;

    const isIntra = stateType === "intra";

    const tdsAmount = form.tds_enabled ? Math.round(discountedSubtotal * safe(form.tds_rate) / 100 * 100) / 100 : 0;
    const tcsAmount = form.tcs_enabled ? Math.round(grandTotal * safe(form.tcs_rate) / 100 * 100) / 100 : 0;
    const netPayable = Math.round((grandTotal - tdsAmount + tcsAmount) * 100) / 100;

    const hsnSummary = Object.values(hsnMap).map(h => ({
      ...h,
      taxableValue: Math.round(h.taxableValue * 100) / 100,
      cgst: Math.round(h.cgst * 100) / 100,
      sgst: Math.round(h.sgst * 100) / 100,
      igst: Math.round(h.igst * 100) / 100,
      total: Math.round(h.total * 100) / 100,
    }));

    return {
      rawSubtotal: Math.round(rawSubtotal * 100) / 100,
      lineDiscountTotal: Math.round(totalLineDiscount * 100) / 100,
      postLinePool: Math.round(postLinePool * 100) / 100,
      discount: Math.round(overall * 100) / 100,
      overallRatio,
      subtotal: Math.round(discountedSubtotal * 100) / 100,   // Taxable Value
      taxAmount: Math.round(taxAmount * 100) / 100,
      totalInclTax: Math.round(totalInclTax * 100) / 100,
      nonGstTotal: Math.round(nonGstTotal * 100) / 100,
      cgst: isIntra ? Math.round(taxAmount * 50) / 100 : 0,
      sgst: isIntra ? Math.round(taxAmount * 50) / 100 : 0,
      igst: isIntra ? 0 : Math.round(taxAmount * 100) / 100,
      grandTotal: Math.round(grandTotal * 100) / 100,
      tdsAmount,
      tcsAmount,
      netPayable,
      isIntra,
      hsnSummary,
      enriched,  // expose for preview row rendering
    };
  }, [items, form.discount, form.shipping, form.previous_balance, form.round_off, stateType, form.tds_enabled, form.tds_rate, form.tcs_enabled, form.tcs_rate, isGstRegistered]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(amount || 0);

  const openRegistrationWall = (intent) => {
    // Persist current draft + intent so we can resume after signup
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, items, inlineSeller, stateType, savedAt: new Date().toISOString() }));
      localStorage.setItem(PENDING_INTENT_KEY, JSON.stringify({ intent, at: Date.now() }));
    } catch { /* quota */ }
    setPendingIntent(intent);
    setShowRegWall(true);
    try { window.gtag?.("event", "signup_prompt_shown", { intent }); } catch { /* ignore */ }
  };

  const handleSave = async () => {
    // Quick Invoice: allow without company_id if inline seller has at least a business name
    if (!form.company_id && !isAnonymous) {
      const seller_name = inlineSeller.company_name.trim();
      if (!seller_name) { toast.error("Enter your business/seller name to continue"); return; }
    }
    if (isAnonymous && !inlineSeller.company_name.trim()) {
      toast.error("Enter your business/seller name to continue"); return;
    }
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!items.some(i => i.name.trim())) { toast.error("Add at least one item"); return; }
    // Gate anonymous: open Registration Wall instead of hitting the API
    if (isAnonymous) {
      openRegistrationWall("save");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        // Only send company_id when set; otherwise attach inline_seller so backend auto-creates one.
        company_id: form.company_id || null,
        inline_seller: form.company_id ? null : {
          company_name: inlineSeller.company_name.trim(),
          gst_number: (inlineSeller.gst_number || "").trim().toUpperCase() || null,
          address: inlineSeller.address.trim() || null,
          state: inlineSeller.state.trim() || null,
          phone: inlineSeller.phone.trim() || null,
          email: inlineSeller.email.trim() || null,
        },
        items: items.filter(i => i.name.trim()).map(i => ({
          name: i.name,
          description: i.description || "",
          hsn_code: i.hsn_code || "",
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
          gst_percent: Number(i.gst_percent) || 0,
          gst_not_applicable: !!i.gst_not_applicable,
          line_discount: Number(i.line_discount) || 0,
          line_discount_percent: Number(i.line_discount_percent) || 0,
        })),
        state_type: stateType,
        cgst: totals.cgst,
        sgst: totals.sgst,
        igst: totals.igst,
        tds_section: form.tds_enabled ? form.tds_section : null,
        tds_rate: form.tds_enabled ? form.tds_rate : 0,
        tds_amount: totals.tdsAmount,
        tcs_rate: form.tcs_enabled ? form.tcs_rate : 0,
        tcs_amount: totals.tcsAmount,
        round_off: form.round_off || 0,
        place_of_supply: form.place_of_supply || "",
        customer_id: form.customer_id || null,
        source_quotation_ids: form.source_quotation_ids || [],
      };
      const res = await axios.post(`${API}/builder/invoices/create`, payload, { withCredentials: true });
      setCreatedInvoice(res.data);
      // Clear the anonymous draft — this invoice is now persisted
      try {
        localStorage.removeItem(DRAFT_KEY);
        localStorage.removeItem(PENDING_INTENT_KEY);
      } catch { /* ignore */ }
      // GA4: activation event — first invoice saved (safe no-op if gtag absent)
      try { window.gtag?.("event", "first_invoice_saved", { invoice_id: res.data?.invoice_id }); } catch { /* ignore */ }
      // Also mark anonymous-invoice-completed if this session started anonymously
      try {
        if (sessionStorage.getItem("anon_started_fired")) {
          window.gtag?.("event", "anonymous_invoice_completed", { invoice_id: res.data?.invoice_id });
        }
      } catch { /* ignore */ }
      // If backend auto-created the company from inline seller, silently confirm — no modal.
      if (res.data?.company_auto_created) {
        toast.success("Business details saved. You won't have to enter them again.", { duration: 4500 });
        setSellerPersisted(true);
        try { window.gtag?.("event", "company_profile_saved", { source: "quick_invoice" }); } catch { /* ignore */ }
        // Refresh companies so next invoice uses the dropdown (isQuickInvoiceMode flips to false)
        try {
          const cRes = await axios.get(`${API}/companies`, { withCredentials: true });
          const list = cRes.data?.companies || [];
          setCompanies(list);
          const newId = res.data?.company_id || list[0]?.company_id;
          if (newId) setForm(f => ({ ...f, company_id: newId }));
        } catch { /* non-blocking */ }
      } else {
        toast.success("Invoice created!");
      }

      // ── Inline save to master (parallel, best-effort, non-blocking) ──
      const persistedCompanyId = res.data?.company_id || form.company_id;
      const masterTasks = [];
      // Save customer to master if opted in and not already linked
      if (saveCustomerToMaster && form.customer_name.trim() && !form.customer_id) {
        masterTasks.push(
          axios.post(`${API}/customers/`, {
            name: form.customer_name,
            gstin: form.customer_gst || null,
            address: form.customer_address || null,
            email: form.customer_email || null,
            phone: form.customer_phone || null,
            company_id: persistedCompanyId,
          }, { withCredentials: true }).catch(() => null)
        );
      }
      // Save each opted-in product/service to master (skip ones already in catalog)
      items.forEach(it => {
        if (it.save_to_master !== false && it.name.trim() && !isInCatalog(it)) {
          masterTasks.push(
            axios.post(`${API}/masters/products`, {
              name: it.name,
              hsn_code: it.hsn_code || "",
              unit: it.unit || null,
              kind: it.kind || "product",
              default_gst_rate: it.gst_percent || 18,
              default_price: it.unit_price || null,
            }, { withCredentials: true }).catch(() => null)
          );
        }
      });
      if (masterTasks.length > 0) {
        Promise.all(masterTasks).then(results => {
          const okCount = results.filter(r => r && r.data).length;
          if (okCount > 0) {
            toast.success(`Saved ${okCount} ${okCount === 1 ? "item" : "items"} to your master`, { duration: 3000 });
            // Refresh masters so subsequent invoices show them in dropdowns
            fetchCustomers();
            fetchProducts();
          }
        });
      }

      // Also create a recurring template if user opted in
      if (form.make_recurring && form.rec_start_date) {
        try {
          await axios.post(`${API}/recurring/create`, {
            company_id: form.company_id,
            customer_name: form.customer_name,
            customer_gst: form.customer_gst || "",
            customer_email: form.customer_email || "",
            customer_id: form.customer_id || null,
            items: items.filter(i => i.name.trim()).map(i => ({
              name: i.name,
              description: i.description || "",
              hsn_code: i.hsn_code || "",
              quantity: Number(i.quantity) || 0,
              unit_price: Number(i.unit_price) || 0,
              gst_percent: Number(i.gst_percent) || 0,
            })),
            frequency: form.rec_frequency,
            start_date: form.rec_start_date,
            end_date: form.rec_end_date || null,
            notes: form.notes || "",
            is_active: true,
            auto_send: form.rec_auto_send,
          }, { withCredentials: true });
          toast.success("Recurring schedule created");
        } catch (recErr) {
          console.error("Recurring save failed:", recErr);
          toast.error("Invoice saved, but recurring schedule failed");
        }
      }

      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="invoice-builder-page">
      {isAnonymous && (
        <SEO
          title="Free GST Invoice Generator India — Create & Download Invoice Online | InvoiceSetu"
          description="Create a GST-compliant invoice online in under 60 seconds. Free invoice generator for Indian businesses — no signup required. Download PDF, share with customers instantly."
          canonical={`${window.location.origin}/create-invoice`}
          keywords="free gst invoice generator, create invoice online, invoice generator india, gst bill format, tax invoice generator, free invoice maker india"
        />
      )}
      <div className="max-w-4xl mx-auto">
        {isAnonymous && (
          <div className="mb-4 rounded-xl bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 px-4 py-3 flex items-start gap-3" data-testid="anon-banner">
            <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="text-sm">
              <p className="font-semibold text-slate-900">You're using InvoiceSetu without signing in</p>
              <p className="text-xs text-slate-600 mt-0.5">Fill in the invoice — we'll ask you to create a free account only when you're ready to download or save.</p>
            </div>
          </div>
        )}
        {/* Steps */}
        <div className="flex items-center gap-2 mb-6">
          {[{ n: 1, label: "Details" }, { n: 2, label: "Items" }, { n: 3, label: "Preview" }].map(s => (
            <div key={s.n} className="flex items-center gap-2 flex-1">
              <button
                onClick={() => setStep(s.n)}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step >= s.n ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
                }`}
              >
                {step > s.n ? <CheckCircle2 className="w-4 h-4" /> : s.n}
              </button>
              <span className={`text-sm font-medium hidden sm:block ${step >= s.n ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
              {s.n < 3 && <div className={`flex-1 h-0.5 ${step > s.n ? "bg-blue-600" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-6 space-y-5">
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Invoice Details</h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Company {companies.length === 0 ? "" : "*"}</label>
                {companies.length > 0 ? (
                  <Select value={form.company_id} onValueChange={v => update("company_id", v)}>
                    <SelectTrigger data-testid="field-company"><SelectValue placeholder="Select company..." /></SelectTrigger>
                    <SelectContent>
                      {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2" data-testid="quick-invoice-hint">
                    Quick Invoice mode — fill your business details below. We'll save them automatically for your next invoice.
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Type</label>
                <Select value={form.invoice_type} onValueChange={v => update("invoice_type", v)}>
                  <SelectTrigger data-testid="field-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales Invoice</SelectItem>
                    <SelectItem value="purchase">Purchase Invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quick Invoice — inline seller details */}
            {isQuickInvoiceMode && (
              <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-4" data-testid="inline-seller-section">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-base font-semibold text-slate-800">Your Business Details</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {inlineSeller.gst_number.trim()
                        ? "GSTIN detected — this will be issued as a Tax Invoice."
                        : "Skip GSTIN to issue a simple Bill (no GST). You can add GSTIN later."}
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Business / Seller Name *</label>
                    <Input
                      value={inlineSeller.company_name}
                      onChange={e => setInlineSeller(s => ({ ...s, company_name: e.target.value }))}
                      placeholder="Acme Traders Pvt Ltd"
                      data-testid="inline-seller-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">GSTIN (optional)</label>
                    <Input
                      value={inlineSeller.gst_number}
                      onChange={e => setInlineSeller(s => ({ ...s, gst_number: e.target.value.toUpperCase() }))}
                      placeholder="29AABCT1234A1Z5"
                      maxLength={15}
                      className="font-mono uppercase"
                      data-testid="inline-seller-gstin"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Mobile (optional)</label>
                    <Input
                      value={inlineSeller.phone}
                      onChange={e => setInlineSeller(s => ({ ...s, phone: e.target.value }))}
                      placeholder="+91 98xxxxxx"
                      data-testid="inline-seller-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Email (optional)</label>
                    <Input
                      value={inlineSeller.email}
                      onChange={e => setInlineSeller(s => ({ ...s, email: e.target.value }))}
                      placeholder="you@business.com"
                      data-testid="inline-seller-email"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Address (optional)</label>
                    <Input
                      value={inlineSeller.address}
                      onChange={e => setInlineSeller(s => ({ ...s, address: e.target.value }))}
                      placeholder="Shop 12, MG Road, Bengaluru — 560001"
                      data-testid="inline-seller-address"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Invoice Date *</label>
                <Input type="date" value={form.invoice_date} onChange={e => update("invoice_date", e.target.value)} data-testid="field-invoice_date" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Due Date</label>
                <Input type="date" value={form.due_date} onChange={e => update("due_date", e.target.value)} data-testid="field-due_date" />
              </div>
            </div>

            <hr className="border-slate-100" />
            <h3 className="text-base font-semibold text-slate-800">Customer / Client</h3>

            {/* Customer search/select */}
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Search or Add Customer *</label>
              <Input
                value={custSearch}
                onChange={e => {
                  setCustSearch(e.target.value);
                  update("customer_name", e.target.value);
                  setShowCustDropdown(e.target.value.length > 0);
                }}
                placeholder="Type customer name or GSTIN..."
                data-testid="customer-search"
              />
              {showCustDropdown && customers.filter(c => c.name.toLowerCase().includes(custSearch.toLowerCase()) || (c.gstin && c.gstin.includes(custSearch.toUpperCase()))).length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {customers.filter(c => c.name.toLowerCase().includes(custSearch.toLowerCase()) || (c.gstin && c.gstin.includes(custSearch.toUpperCase()))).map(c => (
                    <button
                      key={c.customer_id}
                      type="button"
                      onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2.5 hover:bg-slate-50 text-sm border-b border-slate-50 last:border-0"
                      data-testid={`cust-option-${c.customer_id}`}
                    >
                      <span className="font-medium text-slate-900">{c.name}</span>
                      {c.gstin && <span className="text-slate-500 ml-2 font-mono text-xs">{c.gstin}</span>}
                      {c.state && <span className="text-slate-400 ml-2 text-xs">{c.state}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Customer GSTIN
                  {form.customer_gst.length === 15 && stateType && (
                    <span className={`ml-2 text-xs font-bold px-1.5 py-0.5 rounded ${stateType === "intra" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {stateType === "intra" ? "Intra-state (CGST+SGST)" : "Inter-state (IGST)"}
                    </span>
                  )}
                </label>
                <Input value={form.customer_gst} onChange={e => update("customer_gst", e.target.value.toUpperCase())} placeholder="29AABCT1234A1Z5" data-testid="field-customer_gst" className="font-mono uppercase" maxLength={15} />
                {form.customer_gst && validateGSTIN(form.customer_gst) && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1" data-testid="gstin-error">
                    <AlertCircle className="w-3 h-3" /> {validateGSTIN(form.customer_gst)}
                  </p>
                )}
                {form.customer_gst && !validateGSTIN(form.customer_gst) && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Valid GSTIN format
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Place of Supply</label>
                <Input value={form.place_of_supply} onChange={e => update("place_of_supply", e.target.value)} placeholder="e.g. Karnataka" data-testid="field-place_of_supply" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Customer Address</label>
                <Input value={form.customer_address} onChange={e => update("customer_address", e.target.value)} placeholder="456 Brigade Road, Bangalore" data-testid="field-customer_address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                  <Input value={form.customer_email} onChange={e => update("customer_email", e.target.value)} placeholder="email@co.in" data-testid="field-customer_email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
                  <Input value={form.customer_phone} onChange={e => update("customer_phone", e.target.value)} placeholder="+91..." data-testid="field-customer_phone" />
                </div>
              </div>
            </div>

            {/* Optional milestone billing — link this invoice to existing quotation(s) */}
            {availableQuotations.length > 0 && (
              <div className="border border-blue-100 bg-blue-50/30 rounded-xl p-4 mb-4" data-testid="quotation-link-section">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Link this invoice to quotation(s) <span className="text-slate-400 normal-case font-normal">— optional</span></p>
                <p className="text-xs text-slate-600 mb-3">This customer has open quotations. Linking will track milestone billing — the quote's "Remaining To Invoice" reduces accordingly.</p>
                <div className="space-y-2">
                  {availableQuotations.map(qq => {
                    const checked = form.source_quotation_ids.includes(qq.quotation_id);
                    return (
                      <label
                        key={qq.quotation_id}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${checked ? "bg-blue-50 border-blue-300" : "bg-white border-slate-200 hover:border-slate-300"}`}
                        data-testid={`q-link-row-${qq.quotation_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            update("source_quotation_ids",
                              checked
                                ? form.source_quotation_ids.filter(x => x !== qq.quotation_id)
                                : [...form.source_quotation_ids, qq.quotation_id]
                            );
                          }}
                          className="rounded text-blue-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{qq.quotation_number}</p>
                          <p className="text-[11px] text-slate-500">Quote {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(qq.grand_total)} · Remaining to invoice {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(qq.remaining_to_invoice)}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {loadingQuotations && form.customer_name && (
              <p className="text-[11px] text-slate-400 mb-2" data-testid="quotation-link-loading">Looking up open quotations…</p>
            )}

            {/* Save to customer master — inline opt-in checkbox (only visible to logged-in users) */}
            {!isAnonymous && form.customer_name.trim() && !form.customer_id && (
              <label
                className="text-xs text-slate-600 flex items-center gap-2 select-none cursor-pointer"
                data-testid="save-customer-checkbox-label"
              >
                <input
                  type="checkbox"
                  checked={saveCustomerToMaster}
                  onChange={e => setSaveCustomerToMaster(e.target.checked)}
                  className="w-3.5 h-3.5 accent-blue-600"
                  data-testid="save-customer-checkbox"
                />
                <BookmarkPlus className="w-3.5 h-3.5 text-blue-600" />
                Save <span className="font-medium">"{form.customer_name}"</span> to my customer master
              </label>
            )}

            <div className="flex justify-end pt-4">
              <Button onClick={() => {
                if (isQuickInvoiceMode) {
                  if (!inlineSeller.company_name.trim()) { toast.error("Enter your business/seller name"); return; }
                  const gst = (inlineSeller.gst_number || "").trim();
                  if (gst && validateGSTIN(gst)) { toast.error(validateGSTIN(gst)); return; }
                } else if (!form.company_id) {
                  toast.error("Select a company"); return;
                }
                if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
                if (form.customer_gst && validateGSTIN(form.customer_gst)) {
                  toast.error(validateGSTIN(form.customer_gst));
                  return;
                }
                setStep(2);
              }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="next-to-items">
                Next: Add Items <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Items */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>Line Items</h2>

              {items.map((item, i) => {
                const isService = (item.kind || "product") === "service";
                return (
                <div key={i} className="flex flex-col sm:flex-row gap-3 mb-4 p-3 sm:p-4 bg-slate-50 rounded-xl border border-slate-100" data-testid={`item-row-${i}`}>
                  <div className={`flex-1 grid grid-cols-3 ${isGstRegistered ? "sm:grid-cols-7" : "sm:grid-cols-6"} gap-2 sm:gap-3`}>
                    {/* ── Product / Service toggle (full width) ── */}
                    <div className="col-span-3 sm:col-span-7 flex items-center gap-1.5 -mb-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1">Type:</span>
                      <button
                        type="button"
                        onClick={() => updateItem(i, "kind", "product")}
                        className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full transition-colors ${!isService ? "bg-blue-100 text-blue-700 border border-blue-200" : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"}`}
                        data-testid={`item-kind-product-${i}`}
                      >Product</button>
                      <button
                        type="button"
                        onClick={() => {
                          updateItem(i, "kind", "service");
                          updateItem(i, "quantity", 1);
                          updateItem(i, "unit", "");
                        }}
                        className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full transition-colors ${isService ? "bg-violet-100 text-violet-700 border border-violet-200" : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"}`}
                        data-testid={`item-kind-service-${i}`}
                      >Service</button>
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <label className="block text-xs text-slate-500 mb-1">Item Name</label>
                      <Input value={item.name} onChange={e => updateItem(i, "name", e.target.value)} placeholder={isService ? "Service" : "Product"} className="text-sm" />
                      <Input value={item.description || ""} onChange={e => updateItem(i, "description", e.target.value)} placeholder="Description (optional)" className="text-xs mt-1 text-slate-500" data-testid={`item-desc-${i}`} />
                      {products.length > 0 && !item.name && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {products.slice(0, 3).map(p => (
                            <button
                              key={p.product_id}
                              type="button"
                              onClick={() => selectProduct(i, p)}
                              className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                            >
                              {p.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-span-3 sm:col-span-1 relative">
                      <label className="block text-xs text-slate-500 mb-1">{isService ? "SAC" : "HSN/SAC"}</label>
                      <HSNInput
                        value={item.hsn_code || ""}
                        onChange={(code, rate) => {
                          updateItem(i, "hsn_code", code);
                          if (rate !== undefined) updateItem(i, "gst_percent", rate);
                        }}
                      />
                    </div>
                    {!isService && (
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Qty</label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={e => updateItem(i, "quantity", parseFloat(e.target.value) || 0)}
                          className="text-sm"
                          min="0"
                          data-testid={`item-qty-${i}`}
                        />
                        <Input
                          value={item.unit || ""}
                          onChange={e => updateItem(i, "unit", e.target.value)}
                          placeholder="Unit (kg, pcs)"
                          className="text-[11px] mt-1 h-7 px-2 text-slate-500"
                          data-testid={`item-unit-${i}`}
                        />
                      </div>
                    )}
                    <div className={isService ? "col-span-1 sm:col-span-2" : ""}>
                      <label className="block text-xs text-slate-500 mb-1">{isService ? "Amount (₹)" : "Rate (₹)"}</label>
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={e => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)}
                        className="text-sm"
                        min="0"
                        data-testid={`item-rate-${i}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1" title="Line-item discount (₹) — applied before overall discount and GST">
                        Disc (₹)
                      </label>
                      <Input
                        type="number"
                        value={item.line_discount || 0}
                        onChange={e => updateItem(i, "line_discount", parseFloat(e.target.value) || 0)}
                        className="text-sm"
                        min="0"
                        placeholder="0"
                        data-testid={`item-line-discount-${i}`}
                      />
                    </div>
                    {isGstRegistered && (
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">GST %</label>
                        <Input
                          type="number"
                          value={item.gst_not_applicable ? "" : item.gst_percent}
                          onChange={e => updateItem(i, "gst_percent", parseFloat(e.target.value) || 0)}
                          className={`text-sm ${item.gst_not_applicable ? "bg-slate-50 text-slate-300" : ""}`}
                          min="0"
                          disabled={item.gst_not_applicable}
                          placeholder={item.gst_not_applicable ? "N/A" : "18"}
                        />
                      </div>
                    )}
                    {isGstRegistered && (
                      <div className="flex flex-col items-center justify-end gap-1 min-w-[60px]">
                        <label className="flex items-center gap-1.5 cursor-pointer group" title="Use for reimbursement or pass-through charges where GST should not be applied.">
                          <input
                            type="checkbox"
                            checked={item.gst_not_applicable || false}
                            onChange={e => updateItem(i, "gst_not_applicable", e.target.checked)}
                            className="rounded text-amber-600 w-3.5 h-3.5"
                            data-testid={`gst-na-${i}`}
                          />
                          <span className="text-[10px] font-medium text-slate-500 group-hover:text-amber-700 leading-tight">Non-GST</span>
                        </label>
                        {item.gst_not_applicable && (
                          <span className="text-[9px] text-amber-600 leading-tight text-center">No tax applied</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-end justify-between sm:justify-end gap-2">
                    <div className="text-right min-w-[80px]">
                      <label className="block text-xs text-slate-500 mb-1">Total</label>
                      <p className={`text-sm font-semibold py-2 ${item.gst_not_applicable ? "text-amber-700" : "text-slate-900"}`} data-testid={`item-total-${i}`}>
                        {(() => {
                          const isSvc = (item.kind || "product") === "service";
                          const qty = isSvc ? 1 : (Number(item.quantity) || 0);
                          const rate = Number(item.unit_price) || 0;
                          const gross = qty * rate;
                          const ld = Math.min(Number(item.line_discount) || 0, gross);
                          const postLine = gross - ld;
                          if (!isGstRegistered || item.gst_not_applicable) return formatCurrency(postLine);
                          const gst = (Number(item.gst_percent) || 0) / 100;
                          return formatCurrency(postLine * (1 + gst));
                        })()}
                      </p>
                      {Number(item.line_discount) > 0 && (
                        <p className="text-[10px] text-red-500 leading-tight">
                          Line disc -{formatCurrency(Number(item.line_discount))}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!isAnonymous && item.name.trim() && !isInCatalog(item) && (
                        <label
                          className="flex items-center gap-1 text-[10px] text-slate-500 select-none cursor-pointer pr-1"
                          title={`Save ${item.kind === 'service' ? 'service' : 'product'} to master`}
                          data-testid={`save-catalog-label-${i}`}
                        >
                          <input
                            type="checkbox"
                            checked={item.save_to_master !== false}
                            onChange={e => updateItem(i, "save_to_master", e.target.checked)}
                            className="w-3 h-3 accent-emerald-600"
                            data-testid={`save-catalog-${i}`}
                          />
                          <BookmarkPlus className="w-3 h-3 text-emerald-600" />
                          <span className="hidden sm:inline">Save</span>
                        </label>
                      )}
                      <button onClick={() => removeItem(i)} className="p-2 text-slate-400 hover:text-red-500" disabled={items.length <= 1}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
              })}

              <Button variant="outline" onClick={addItem} className="w-full mt-2 border-dashed" data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>

            {/* Totals + extras */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Overall Discount (₹)</label>
                  <Input type="number" value={form.discount} onChange={e => update("discount", parseFloat(e.target.value) || 0)} min="0" data-testid="field-discount" />
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    Applied AFTER per-line discounts, BEFORE GST. <span className="text-slate-400">Proportionately distributed across GST items.</span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Shipping (₹)</label>
                  <Input type="number" value={form.shipping} onChange={e => update("shipping", parseFloat(e.target.value) || 0)} min="0" data-testid="field-shipping" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Round Off (₹)</label>
                  <Input type="number" value={form.round_off} onChange={e => update("round_off", parseFloat(e.target.value) || 0)} step="0.01" data-testid="field-round-off" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Previous Balance (₹)</label>
                  {pendingBalance && pendingBalance.amount > 0 ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                        <input
                          type="checkbox"
                          checked={includeBalance}
                          onChange={e => setIncludeBalance(e.target.checked)}
                          className="rounded"
                          data-testid="include-balance-checkbox"
                        />
                        <div className="text-xs">
                          <span className="font-medium text-amber-800">
                            {formatCurrency(pendingBalance.amount)} pending
                          </span>
                          <span className="text-amber-600 ml-1">
                            ({pendingBalance.count} unpaid invoice{pendingBalance.count !== 1 ? "s" : ""})
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">{includeBalance ? "Included in total" : "Check to include in this invoice"}</p>
                    </div>
                  ) : (
                    <>
                      <Input type="number" value={form.previous_balance} onChange={e => update("previous_balance", parseFloat(e.target.value) || 0)} min="0" data-testid="field-previous-balance" />
                      <p className="text-xs text-slate-400 mt-1">
                        {form.customer_id ? "No pending balance" : "Select a customer to auto-calculate"}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* TDS/TCS Section */}
              <div className="border border-slate-100 rounded-xl p-4 mb-6 space-y-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">TDS / TCS (Optional)</p>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={form.tds_enabled} onChange={e => update("tds_enabled", e.target.checked)} className="rounded" data-testid="tds-toggle" />
                    Apply TDS
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={form.tcs_enabled} onChange={e => update("tcs_enabled", e.target.checked)} className="rounded" data-testid="tcs-toggle" />
                    Apply TCS
                  </label>
                </div>
                {form.tds_enabled && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">TDS Section</label>
                      <select
                        value={form.tds_section}
                        onChange={e => {
                          const sec = tdsSections.find(s => s.section === e.target.value);
                          update("tds_section", e.target.value);
                          if (sec) update("tds_rate", sec.individual_rate);
                        }}
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                        data-testid="tds-section-select"
                      >
                        <option value="">Select section</option>
                        {tdsSections.map(s => (
                          <option key={s.section} value={s.section}>{s.section} — {s.description} ({s.individual_rate}%)</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">TDS Rate (%)</label>
                      <Input type="number" value={form.tds_rate} onChange={e => update("tds_rate", parseFloat(e.target.value) || 0)} min="0" max="100" data-testid="tds-rate" />
                    </div>
                  </div>
                )}
                {form.tcs_enabled && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">TCS Rate (%)</label>
                      <Input type="number" value={form.tcs_rate} onChange={e => update("tcs_rate", parseFloat(e.target.value) || 0)} min="0" max="100" data-testid="tcs-rate" />
                    </div>
                  </div>
                )}
              </div>

              {/* Recurring Section (P2.8 — simplified in-builder recurring) */}
              <div className="border border-slate-100 rounded-xl p-4 mb-6 space-y-3" data-testid="recurring-section">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.make_recurring}
                    onChange={e => {
                      update("make_recurring", e.target.checked);
                      if (e.target.checked && !form.rec_start_date) {
                        update("rec_start_date", form.invoice_date || new Date().toISOString().split("T")[0]);
                      }
                    }}
                    className="rounded"
                    data-testid="make-recurring-toggle"
                  />
                  Make this recurring
                  <span className="text-[10px] text-slate-400 font-normal">— auto-generate on a schedule</span>
                </label>
                {form.make_recurring && (
                  <div className="grid sm:grid-cols-4 gap-3" data-testid="recurring-options">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Frequency</label>
                      <select
                        value={form.rec_frequency}
                        onChange={e => update("rec_frequency", e.target.value)}
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                        data-testid="rec-frequency"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Start Date</label>
                      <Input
                        type="date"
                        value={form.rec_start_date}
                        onChange={e => update("rec_start_date", e.target.value)}
                        data-testid="rec-start-date"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">End Date <span className="text-slate-400">(optional)</span></label>
                      <Input
                        type="date"
                        value={form.rec_end_date}
                        onChange={e => update("rec_end_date", e.target.value)}
                        placeholder="Until cancelled"
                        data-testid="rec-end-date"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pb-2">
                        <input
                          type="checkbox"
                          checked={form.rec_auto_send}
                          onChange={e => update("rec_auto_send", e.target.checked)}
                          className="rounded"
                          data-testid="rec-auto-send"
                        />
                        Auto-send to customer
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                {/* ── Tax Summary ── */}
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{isGstRegistered ? "Tax Summary" : "Summary"}</p>
                <div className="flex justify-between"><span className="text-slate-500">{isGstRegistered ? "Subtotal (Excl. Tax)" : "Subtotal"}</span><span className="font-medium">{formatCurrency(totals.rawSubtotal)}</span></div>
                {totals.lineDiscountTotal > 0 && <div className="flex justify-between"><span className="text-slate-500">Line-item Discounts</span><span className="font-medium text-red-500">-{formatCurrency(totals.lineDiscountTotal)}</span></div>}
                {totals.discount > 0 && <div className="flex justify-between"><span className="text-slate-500">Overall Discount</span><span className="font-medium text-red-500">-{formatCurrency(totals.discount)}</span></div>}
                {(totals.discount > 0 || totals.lineDiscountTotal > 0) && <div className="flex justify-between"><span className="text-slate-500">{isGstRegistered ? "Taxable Value" : "Net Amount"}</span><span className="font-medium">{formatCurrency(totals.subtotal)}</span></div>}
                {isGstRegistered && (totals.isIntra ? (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500">CGST</span><span className="font-medium">{formatCurrency(totals.cgst)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">SGST</span><span className="font-medium">{formatCurrency(totals.sgst)}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between"><span className="text-slate-500">IGST</span><span className="font-medium">{formatCurrency(totals.igst)}</span></div>
                ))}
                {isGstRegistered && (
                  <div className="flex justify-between pt-1 border-t border-dashed border-slate-100">
                    <span className="text-slate-600 font-medium">Total (Incl. Tax)</span>
                    <span className="font-semibold">{formatCurrency(totals.totalInclTax)}</span>
                  </div>
                )}

                {/* ── Other Charges / Non-GST ── */}
                {(totals.nonGstTotal > 0 || form.shipping > 0 || form.round_off !== 0 || form.previous_balance > 0) && (
                  <>
                    <div className="pt-2 mt-2 border-t border-slate-100">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Other Charges</p>
                    </div>
                    {totals.nonGstTotal > 0 && <div className="flex justify-between"><span className="text-amber-700">Non-GST / Reimbursements</span><span className="font-medium text-amber-700">+{formatCurrency(totals.nonGstTotal)}</span></div>}
                    {form.shipping > 0 && <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="font-medium">+{formatCurrency(form.shipping)}</span></div>}
                    {form.round_off !== 0 && <div className="flex justify-between"><span className="text-slate-500">Round Off</span><span className="font-medium">{formatCurrency(form.round_off)}</span></div>}
                    {form.previous_balance > 0 && <div className="flex justify-between"><span className="text-slate-500">Previous Balance</span><span className="font-medium">+{formatCurrency(form.previous_balance)}</span></div>}
                  </>
                )}

                {/* ── Grand Total ── */}
                <div className="flex justify-between pt-3 border-t-2 border-slate-200 font-bold text-base">
                  <span className="text-slate-900">Grand Total</span>
                  <span className="text-blue-600">{formatCurrency(totals.grandTotal)}</span>
                </div>
                {totals.tdsAmount > 0 && <div className="flex justify-between"><span className="text-slate-500">TDS ({form.tds_section} @ {form.tds_rate}%)</span><span className="font-medium text-red-500">-{formatCurrency(totals.tdsAmount)}</span></div>}
                {totals.tcsAmount > 0 && <div className="flex justify-between"><span className="text-slate-500">TCS @ {form.tcs_rate}%</span><span className="font-medium text-emerald-600">+{formatCurrency(totals.tcsAmount)}</span></div>}
                {(totals.tdsAmount > 0 || totals.tcsAmount > 0) && (
                  <div className="flex justify-between pt-2 border-t border-slate-200 text-lg font-bold">
                    <span className="text-slate-900">Net Payable</span>
                    <span className="text-blue-600">{formatCurrency(totals.netPayable)}</span>
                  </div>
                )}
                {/* Amount in Words */}
                <div className="pt-3 border-t border-dashed border-slate-200" data-testid="amount-in-words">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">Amount in Words</p>
                  <p className="text-sm font-medium text-slate-700 italic">{amountInWords(totals.netPayable || totals.grandTotal)}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="back-to-details"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={() => setStep(3)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="next-to-preview">
                Preview <Eye className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8" data-testid="invoice-preview">
              {/* Header */}
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {form.invoice_type === "sales" ? "TAX INVOICE" : "PURCHASE INVOICE"}
                  </h2>
                  {selectedCompany && (
                    <div className="mt-2 text-sm text-slate-600 space-y-0.5">
                      <p className="font-semibold text-slate-900">{selectedCompany.company_name}</p>
                      {selectedCompany.address && <p>{selectedCompany.address}</p>}
                      {selectedCompany.gst_number && <p className="font-mono text-xs">GSTIN: {selectedCompany.gst_number}</p>}
                      {selectedCompany.pan_number && <p className="text-xs">PAN: {selectedCompany.pan_number}</p>}
                      {selectedCompany.email && <p className="text-xs">{selectedCompany.email}</p>}
                      {/* Custom business fields */}
                      {selectedCompany.custom_fields?.filter(f => f.field_name && f.field_value).map((f, i) => (
                        <p key={i} className="text-xs"><span className="font-medium">{f.field_name}:</span> {f.field_value}</p>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right text-sm text-slate-600 space-y-0.5">
                  <p><span className="font-medium">Invoice Date:</span> {formatIndianDate(form.invoice_date)}</p>
                  {form.due_date && <p><span className="font-medium">Due Date:</span> {formatIndianDate(form.due_date)}</p>}
                  {form.place_of_supply && <p><span className="font-medium">Place of Supply:</span> {form.place_of_supply}</p>}
                  <p className="text-xs"><span className="font-medium">Type:</span> {stateType === "intra" ? "Intra-State" : "Inter-State"}</p>
                </div>
              </div>

              {/* Bill To */}
              <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Bill To</p>
                <p className="font-semibold text-slate-900">{form.customer_name}</p>
                {form.customer_address && <p className="text-sm text-slate-600">{form.customer_address}</p>}
                {form.customer_gst && <p className="text-sm text-slate-600 font-mono">GSTIN: {form.customer_gst}</p>}
                {form.customer_email && <p className="text-xs text-slate-500">{form.customer_email}</p>}
                {form.customer_phone && <p className="text-xs text-slate-500">Ph: {form.customer_phone}</p>}
              </div>

              {/* Items Table — GST Items */}
              {(() => {
                const servicesOnly = items.length > 0 && items.every(i => (i.kind || "product") === "service");
                return (
              <table className="w-full text-sm mb-4" data-testid="preview-items-table">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="text-left py-2 font-medium">#</th>
                    <th className="text-left py-2 font-medium">Item</th>
                    <th className="text-left py-2 font-medium">{servicesOnly ? "SAC" : "HSN/SAC"}</th>
                    {!servicesOnly && <th className="text-right py-2 font-medium">Qty</th>}
                    <th className="text-right py-2 font-medium">{servicesOnly ? "Amount" : "Rate"}</th>
                    {stateType === "intra" ? (
                      <><th className="text-right py-2 font-medium">CGST</th><th className="text-right py-2 font-medium">SGST</th></>
                    ) : (
                      <th className="text-right py-2 font-medium">IGST</th>
                    )}
                    <th className="text-right py-2 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.filter(i => i.name.trim() && !i.gst_not_applicable).map((item) => {
                    // Find the matching enriched entry (preserves line-discount + overall share)
                    const idx = items.indexOf(item);
                    const enr = (totals.enriched || [])[idx] || {};
                    const gross = enr._gross ?? (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
                    const lineDisc = enr._lineDisc || 0;
                    const postLine = enr._postLine ?? (gross - lineDisc);
                    const overallShare = Math.round(postLine * (totals.overallRatio || 0) * 100) / 100;
                    const taxable = Math.max(0, postLine - overallShare);
                    const gstPct = Number(item.gst_percent) || 0;
                    const tax = Math.round(taxable * gstPct / 100 * 100) / 100;
                    const rowTotal = Math.round((taxable + tax) * 100) / 100;
                    const totalDisc = lineDisc + overallShare;
                    return (
                      <tr key={idx} className="border-b border-slate-50">
                        <td className="py-2.5 text-slate-400">{idx + 1}</td>
                        <td className="py-2.5">
                          <span className="font-medium text-slate-900">{item.name}</span>
                          {item.description && <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>}
                          {totalDisc > 0.01 && (
                            <p className="text-[10px] text-red-500 mt-0.5">
                              {lineDisc > 0 && <>Line -{formatCurrency(lineDisc)} </>}
                              {overallShare > 0 && <>Overall -{formatCurrency(overallShare)}</>}
                            </p>
                          )}
                        </td>
                        <td className="py-2.5 text-slate-600 font-mono text-xs">{item.hsn_code || "—"}</td>
                        {!servicesOnly && (
                          <td className="py-2.5 text-right text-slate-600">{(item.kind || "product") === "service" ? "—" : `${item.quantity}${item.unit ? ` ${item.unit}` : ""}`}</td>
                        )}
                        <td className="py-2.5 text-right text-slate-600">{formatCurrency(item.unit_price)}</td>
                        {stateType === "intra" ? (
                          <><td className="py-2.5 text-right text-slate-600 text-xs">{formatCurrency(tax / 2)}</td><td className="py-2.5 text-right text-slate-600 text-xs">{formatCurrency(tax / 2)}</td></>
                        ) : (
                          <td className="py-2.5 text-right text-slate-600 text-xs">{formatCurrency(tax)}</td>
                        )}
                        <td className="py-2.5 text-right font-medium text-slate-900">{formatCurrency(rowTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
                );
              })()}

              {/* Non-GST Items Section */}
              {items.some(i => i.name.trim() && i.gst_not_applicable) && (
                <div className="mb-6" data-testid="non-gst-section">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Non-GST Items / Reimbursements</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-amber-200 text-amber-700">
                        <th className="text-left py-2 font-medium">#</th>
                        <th className="text-left py-2 font-medium">Description</th>
                        <th className="text-right py-2 font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.filter(i => i.name.trim() && i.gst_not_applicable).map((item, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="py-2 text-slate-400">{i + 1}</td>
                          <td className="py-2 font-medium text-slate-900">{item.name}</td>
                          <td className="py-2 text-right font-medium text-slate-900">{formatCurrency(item.quantity * item.unit_price)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-amber-200 font-semibold">
                        <td></td>
                        <td className="py-2 text-amber-700">Non-GST Total</td>
                        <td className="py-2 text-right text-amber-700">{formatCurrency(totals.nonGstTotal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* HSN/SAC Summary */}
              {totals.hsnSummary.length > 0 && (
                <div className="mb-6" data-testid="hsn-summary">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">HSN/SAC Summary</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500">
                        <th className="text-left py-2 font-medium">HSN/SAC</th>
                        <th className="text-right py-2 font-medium">Taxable Value</th>
                        <th className="text-right py-2 font-medium">Rate</th>
                        {totals.isIntra ? (
                          <><th className="text-right py-2 font-medium">CGST</th><th className="text-right py-2 font-medium">SGST</th></>
                        ) : (
                          <th className="text-right py-2 font-medium">IGST</th>
                        )}
                        <th className="text-right py-2 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {totals.hsnSummary.map((row, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="py-2 font-mono font-medium text-slate-700">{row.code}</td>
                          <td className="py-2 text-right text-slate-600">{formatCurrency(row.taxableValue)}</td>
                          <td className="py-2 text-right text-slate-600">{row.rate}%</td>
                          {totals.isIntra ? (
                            <><td className="py-2 text-right text-slate-600">{formatCurrency(row.cgst)}</td><td className="py-2 text-right text-slate-600">{formatCurrency(row.sgst)}</td></>
                          ) : (
                            <td className="py-2 text-right text-slate-600">{formatCurrency(row.igst)}</td>
                          )}
                          <td className="py-2 text-right font-medium text-slate-800">{formatCurrency(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-72 space-y-1.5 text-sm">
                  {/* Tax Summary */}
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider pb-1">Tax Summary</p>
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal (Excl. Tax)</span><span>{formatCurrency(totals.rawSubtotal)}</span></div>
                  {totals.lineDiscountTotal > 0 && <div className="flex justify-between"><span className="text-slate-500">Line-item Discounts</span><span className="text-red-500">-{formatCurrency(totals.lineDiscountTotal)}</span></div>}
                  {totals.discount > 0 && <div className="flex justify-between"><span className="text-slate-500">Overall Discount</span><span className="text-red-500">-{formatCurrency(totals.discount)}</span></div>}
                  {(totals.discount > 0 || totals.lineDiscountTotal > 0) && <div className="flex justify-between"><span className="text-slate-500">Taxable Value</span><span>{formatCurrency(totals.subtotal)}</span></div>}
                  {totals.isIntra ? (
                    <>
                      <div className="flex justify-between"><span className="text-slate-500">CGST</span><span>{formatCurrency(totals.cgst)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">SGST</span><span>{formatCurrency(totals.sgst)}</span></div>
                    </>
                  ) : (
                    <div className="flex justify-between"><span className="text-slate-500">IGST</span><span>{formatCurrency(totals.igst)}</span></div>
                  )}
                  <div className="flex justify-between pt-1 border-t border-dashed border-slate-100">
                    <span className="text-slate-600 font-medium">Total (Incl. Tax)</span><span className="font-semibold">{formatCurrency(totals.totalInclTax)}</span>
                  </div>

                  {/* Other Charges */}
                  {(totals.nonGstTotal > 0 || form.shipping > 0 || form.previous_balance > 0) && (
                    <>
                      <div className="pt-2 border-t border-slate-100">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider pb-1">Other Charges</p>
                      </div>
                      {totals.nonGstTotal > 0 && <div className="flex justify-between"><span className="text-amber-700">Non-GST / Reimbursements</span><span className="text-amber-700 font-medium">+{formatCurrency(totals.nonGstTotal)}</span></div>}
                      {form.shipping > 0 && <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>+{formatCurrency(form.shipping)}</span></div>}
                      {form.previous_balance > 0 && <div className="flex justify-between"><span className="text-slate-500">Previous Balance</span><span>+{formatCurrency(form.previous_balance)}</span></div>}
                    </>
                  )}

                  {/* Grand Total */}
                  <div className="flex justify-between pt-3 border-t-2 border-slate-300 font-bold text-base">
                    <span>Grand Total</span><span className="text-blue-600">{formatCurrency(totals.grandTotal)}</span>
                  </div>
                  {totals.tdsAmount > 0 && <div className="flex justify-between text-xs"><span className="text-slate-500">TDS ({form.tds_section} @ {form.tds_rate}%)</span><span className="text-red-500">-{formatCurrency(totals.tdsAmount)}</span></div>}
                  {totals.tcsAmount > 0 && <div className="flex justify-between text-xs"><span className="text-slate-500">TCS @ {form.tcs_rate}%</span><span className="text-emerald-600">+{formatCurrency(totals.tcsAmount)}</span></div>}
                  {(totals.tdsAmount > 0 || totals.tcsAmount > 0) && (
                    <div className="flex justify-between pt-2 border-t border-slate-200 font-bold text-lg">
                      <span>Net Payable</span><span className="text-blue-600">{formatCurrency(totals.netPayable)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Amount in Words */}
              <div className="mt-4 px-3 py-2 bg-blue-50 rounded-lg" data-testid="preview-amount-words">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Amount in Words</p>
                <p className="text-sm font-medium text-blue-800">{amountInWords(totals.netPayable || totals.grandTotal)}</p>
              </div>

              {/* Notes/Terms */}
              {(form.notes || form.terms) && (
                <div className="mt-8 pt-6 border-t border-slate-100 text-xs text-slate-500 space-y-1">
                  {form.notes && <p><span className="font-semibold">Notes:</span> {form.notes}</p>}
                  {form.terms && <p><span className="font-semibold">Terms:</span> {form.terms}</p>}
                </div>
              )}

              {/* Bank Details */}
              {selectedCompany?.bank_account_number && (
                <div className="mt-6 p-4 bg-slate-50 rounded-xl text-xs text-slate-600">
                  <p className="font-semibold text-slate-700 mb-1">Bank Details</p>
                  <p>{selectedCompany.bank_name} | A/C: {selectedCompany.bank_account_number} | IFSC: {selectedCompany.bank_ifsc}</p>
                  {selectedCompany.upi_id && <p>UPI: {selectedCompany.upi_id}</p>}
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(2)} data-testid="back-to-items"><ArrowLeft className="w-4 h-4 mr-1" /> Edit Items</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-invoice-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Create Invoice
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Success */}
        {step === 4 && createdInvoice && (() => {
          const isFirstInvoice = !!createdInvoice.company_auto_created;
          const resetBuilder = () => {
            setCreatedInvoice(null);
            setStep(1);
            setForm(prev => ({
              ...prev,
              invoice_date: new Date().toISOString().split("T")[0],
              due_date: "",
              customer_name: "",
              customer_gst: "",
              customer_address: "",
              customer_email: "",
              customer_phone: "",
              customer_id: "",
              notes: "",
              discount: 0,
              shipping: 0,
              round_off: 0,
              previous_balance: 0,
              place_of_supply: "",
              source_quotation_ids: [],
            }));
            setItems([{ name: "", description: "", hsn_code: "", quantity: 1, unit: "", kind: "product", unit_price: 0, gst_percent: 18, gst_not_applicable: false, save_to_master: true }]);
            setSaveCustomerToMaster(true);
            setCustSearch("");
          };
          return (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`rounded-2xl border shadow-sm p-8 text-center ${isFirstInvoice ? "bg-gradient-to-br from-emerald-50 via-white to-blue-50 border-emerald-100" : "bg-white border-slate-100"}`} data-testid="invoice-success-card">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isFirstInvoice ? "bg-white shadow-sm" : "bg-emerald-50"}`}>
              {isFirstInvoice ? <PartyPopper className="w-8 h-8 text-emerald-600" /> : <CheckCircle2 className="w-8 h-8 text-emerald-600" />}
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {isFirstInvoice ? "Congratulations! Your first invoice is ready." : "Invoice Created"}
            </h2>
            {isFirstInvoice && (
              <p className="text-sm text-slate-600 mt-0.5 mb-2">
                Your business details are now saved. Next time will be even faster.
              </p>
            )}
            <p className="text-slate-500 mb-1 font-mono text-sm">{createdInvoice.invoice_number}</p>
            <p className="text-2xl font-bold text-blue-600 mb-6">{formatCurrency(createdInvoice.grand_total)}</p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
              <Button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/builder/invoices/${createdInvoice.invoice_id}/pdf`, {
                      withCredentials: true,
                      responseType: "blob",
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `${(createdInvoice.invoice_number || "invoice").replace(/\//g, "-")}.pdf`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                    // GA4: strong activation signal — user actually USED the invoice
                    try { window.gtag?.("event", "first_invoice_downloaded", { invoice_id: createdInvoice.invoice_id, first: isFirstInvoice }); } catch { /* ignore */ }
                  } catch {
                    toast.error("Failed to download PDF");
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-1" /> Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/share/generate/${createdInvoice.invoice_id}`, {}, { withCredentials: true });
                    const shareUrl = `${window.location.origin}${res.data.share_url}`;
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success("Share link copied to clipboard");
                  } catch {
                    toast.error("Failed to generate share link");
                  }
                }}
                data-testid="share-invoice-btn"
              >
                <Share2 className="w-4 h-4 mr-1" /> Share Invoice
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/invoices/${createdInvoice.invoice_id}`)}
                data-testid="view-invoice-btn"
              >
                <Eye className="w-4 h-4 mr-1" /> View Invoice
              </Button>
              <Button
                onClick={resetBuilder}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="create-new-invoice-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Create New Invoice
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/invoices")}
                data-testid="go-to-invoices-btn"
              >
                All Invoices
              </Button>
            </div>
          </motion.div>
          );
        })()}
      </div>

      {/* Registration Wall — anonymous users hit this on gated actions */}
      <RegistrationWall
        open={showRegWall}
        onClose={() => setShowRegWall(false)}
        intent={pendingIntent || "save"}
        customerName={form.customer_name}
      />
    </div>
  );
}
